/**
 * Email Innovorder
 * Importe un CSV Innovorder (clients de la caisse / commande en ligne),
 * détecte les emails ABSENTS de la base de l'appli Family's (customers),
 * et permet de leur envoyer un email d'invitation depuis le backoffice.
 */
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { sendEmailToList } from '../services/cloudFunctions';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;

const DEFAULT_SUBJECT = "🍔 Le Family's a son appli — et plein d'avantages pour toi !";
const DEFAULT_MESSAGE =
  "Bonjour 👋\n\n" +
  "Bonne nouvelle : Le Family's a maintenant sa propre application !\n" +
  "Commande en quelques secondes, cumule des points de fidélité à chaque commande, " +
  "et profite d'offres réservées aux membres 🍔\n\n" +
  "Télécharge-la maintenant, c'est gratuit 👇";

// Parseur partagé validé (CSV quoté, BOM, déduplication).
const { parseCsv } = require('../utils/innovorderCsv');

const EmailInnovorder = () => {
  const [customerEmails, setCustomerEmails] = useState(null); // Set
  const [loading, setLoading] = useState(true);
  const [fileName, setFileName] = useState('');
  const [csvCount, setCsvCount] = useState(0);
  const [alreadyCount, setAlreadyCount] = useState(0);
  const [newContacts, setNewContacts] = useState([]); // [{email,name}]
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');

  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [testEmail, setTestEmail] = useState('');

  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'customers'));
        const set = new Set();
        snap.forEach((d) => {
          const e = (d.data().email || '').trim().toLowerCase();
          if (e) set.add(e);
        });
        setCustomerEmails(set);
      } catch (e) {
        setError("Impossible de charger la base clients. Réessaie.");
        setCustomerEmails(new Set());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rows = parseCsv(String(e.target.result || ''));
        setCsvCount(rows.length);
        const base = customerEmails || new Set();
        const fresh = rows.filter((r) => !base.has(r.email));
        setAlreadyCount(rows.length - fresh.length);
        setNewContacts(fresh);
        setSelected(new Set(fresh.map((r) => r.email)));
      } catch (err) {
        setError("CSV illisible. Vérifie le fichier.");
      }
    };
    reader.readAsText(file);
  };

  const toggle = (email) => {
    const next = new Set(selected);
    if (next.has(email)) next.delete(email); else next.add(email);
    setSelected(next);
  };

  const filtered = newContacts.filter(
    (c) => c.email.includes(search.toLowerCase()) || (c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.email));
  const toggleAllFiltered = () => {
    const next = new Set(selected);
    if (allFilteredSelected) filtered.forEach((c) => next.delete(c.email));
    else filtered.forEach((c) => next.add(c.email));
    setSelected(next);
  };

  const handleTest = async () => {
    setError(null); setResult(null);
    if (!testEmail || !EMAIL_RE.test(testEmail)) { setError('Renseigne un email de test valide.'); return; }
    if (!subject.trim() || !message.trim()) { setError('Objet et message requis.'); return; }
    setBusy(true);
    try {
      await sendEmailToList({ testEmail: testEmail.trim(), subject, message });
      setResult(`✅ Email de test envoyé à ${testEmail.trim()}`);
    } catch (e) {
      setError(e.message || "Erreur lors de l'envoi du test");
    } finally { setBusy(false); }
  };

  const handleSend = async () => {
    setError(null); setResult(null);
    const emails = [...selected];
    if (emails.length === 0) { setError('Aucun contact sélectionné.'); return; }
    if (!subject.trim() || !message.trim()) { setError('Objet et message requis.'); return; }
    setBusy(true);
    try {
      const res = await sendEmailToList({ emails, subject, message, dryRun: false, source: 'innovorder' });
      setResult(`✅ Envoyé à ${res.sent || 0} contact(s)${res.errors && res.errors.length ? ` (${res.errors.length} lot(s) en erreur)` : ''}.`);
      setConfirming(false);
    } catch (e) {
      setError(e.message || "Erreur lors de l'envoi");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">📧 Email Innovorder</h1>
        <p className="text-gray-600">
          Importe ta base Innovorder (CSV) → on détecte les clients qui n'ont <strong>pas encore l'appli</strong>, et tu les invites par email.
        </p>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">❌ {error}</div>}
      {result && <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">{result}</div>}

      {/* 1. Import CSV */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-3">1. Importer le CSV Innovorder</h2>
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={loading}
          onChange={(e) => handleFile(e.target.files && e.target.files[0])}
          className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-600 file:text-white file:font-medium hover:file:bg-indigo-700"
        />
        {loading && <p className="text-sm text-gray-500 mt-2">Chargement de ta base clients…</p>}
        {fileName && (
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-blue-600">Contacts dans le CSV</p>
              <p className="text-2xl font-bold text-blue-800">{csvCount}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm text-gray-600">Déjà dans l'appli</p>
              <p className="text-2xl font-bold text-gray-700">{alreadyCount}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <p className="text-sm text-green-600">🆕 Nouveaux à inviter</p>
              <p className="text-2xl font-bold text-green-800">{newContacts.length}</p>
            </div>
          </div>
        )}
      </div>

      {newContacts.length > 0 && (
        <>
          {/* 2. Message */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-3">2. Ton email</h2>
            <label className="block text-sm font-medium mb-1">Objet</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className="border rounded px-3 py-2 w-full mb-4" />
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={7} className="border rounded px-3 py-2 w-full" />
            <p className="text-xs text-gray-500 mt-1">Les boutons « Télécharger l'app » (App Store / Google Play) et le lien de désinscription sont ajoutés automatiquement.</p>
            <div className="flex flex-wrap items-end gap-3 mt-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-1">Aperçu : t'envoyer un test</label>
                <input type="email" placeholder="ton@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="border rounded px-3 py-2 w-64" />
              </div>
              <button onClick={handleTest} disabled={busy} className="px-4 py-2 rounded-lg bg-gray-700 text-white font-medium hover:bg-gray-800 disabled:opacity-50">
                {busy ? '…' : '✉️ Envoyer le test'}
              </button>
            </div>
          </div>

          {/* 3. Destinataires */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h2 className="text-xl font-semibold">3. Destinataires ({selected.size} sélectionné(s))</h2>
              <input type="text" placeholder="🔍 filtrer…" value={search} onChange={(e) => setSearch(e.target.value)} className="border rounded px-3 py-1.5 text-sm w-56" />
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 px-3 w-10">
                      <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} />
                    </th>
                    <th className="py-2 px-3">Email</th>
                    <th className="py-2 px-3">Nom</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 1000).map((c) => (
                    <tr key={c.email} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-1.5 px-3"><input type="checkbox" checked={selected.has(c.email)} onChange={() => toggle(c.email)} /></td>
                      <td className="py-1.5 px-3 text-gray-800">{c.email}</td>
                      <td className="py-1.5 px-3 text-gray-500">{c.name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length > 1000 && <p className="text-xs text-gray-400 mt-1">Affichage limité à 1000 lignes (la sélection couvre bien tout le monde).</p>}
          </div>

          {/* 4. Envoi */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            {!confirming ? (
              <button onClick={() => { setError(null); setResult(null); setConfirming(true); }} disabled={busy || selected.size === 0}
                className="px-6 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50">
                📨 Envoyer à {selected.size} contact(s)
              </button>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-medium text-amber-800 mb-3">Confirmer l'envoi de cet email à <strong>{selected.size}</strong> contact(s) ?</p>
                <div className="flex gap-3">
                  <button onClick={handleSend} disabled={busy} className="px-5 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50">
                    {busy ? 'Envoi…' : '✅ Confirmer l\'envoi'}
                  </button>
                  <button onClick={() => setConfirming(false)} disabled={busy} className="px-5 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300">Annuler</button>
                </div>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-3">⚖️ Envoie uniquement à des personnes ayant déjà été tes clientes (Innovorder). Le lien de désinscription est inclus (RGPD).</p>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailInnovorder;
